import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query'
import { Toaster } from "@/components/ui/sonner"
import { toast } from 'sonner'
import './index.css'
import { AuthLayout } from './App'
import LoginPage from './routes/login-page'
import SignupPage from './routes/signup-page'
import VMOSGroupControlPage from './routes/vmos-group-control-page'
import VMOSAgenticGroupPage from './routes/vmos-agentic-group-page'
import ManualDevicesPage from './routes/manual-devices-page'
import ContentUploadPage from './routes/content-upload-page'
import AgentActivityPage from './routes/agent-activity-page'
import BrandsPage from './routes/brands-page'
import PromptsPage from './routes/prompts-page'
import SlackBotsPage from './routes/slack-bots-page'
import SettingsPage from './routes/settings-page'
import ScheduledRunsPage from './routes/scheduled-runs-page'
import SessionsPage from './routes/sessions-page'
import * as Sentry from "@sentry/react";
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { StreamingProvider } from './context/StreamingContext';
import { ThemeProvider } from 'next-themes';
import { ServerReadyGate } from './components/ServerReadyGate';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

Sentry.init({
  dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    posthog.sentryIntegration({
      organization: 'conbersa',
      projectId: 196624,
      severityAllowList: ['error', 'info'] // optional: here is set to handle captureMessage (info) and captureException (error)
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  })
}

// Create a client for React Query with global error handling
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only show error toasts if we already have data in the cache
      // which indicates a failed background update
      if (query.state.data !== undefined) {
        toast.error(`Something went wrong: ${error.message}`)
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})


createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <ServerReadyGate>
        <BrowserRouter>
          <StreamingProvider>
            <Routes>
              <Route element={<AuthLayout />}>
                {/* Routes that require auth state awareness (redirect if logged in/out) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/integrations-status" element={<Navigate to="/runs" replace />} />
                <Route path="/agent-playground/:agentId" element={<Navigate to="/runs" replace />} />
                <Route path="/agent-playground" element={<Navigate to="/runs" replace />} />
                <Route path="/social-accounts" element={<Navigate to="/runs" replace />} />
                <Route path="/device-control" element={<VMOSGroupControlPage />} />
                <Route path="/runs/:groupId" element={<VMOSAgenticGroupPage />} />
                <Route path="/runs" element={<VMOSAgenticGroupPage />} />
                <Route path="/manual" element={<ManualDevicesPage />} />
                <Route path="/content-upload" element={<ContentUploadPage />} />
                <Route path="/agent-activity" element={<AgentActivityPage />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/prompts" element={<PromptsPage />} />
                <Route path="/slack-bots" element={<SlackBotsPage />} /><Route path="/schedule" element={<ScheduledRunsPage />} />
                <Route path="/sessions" element={<SessionsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/reddit-agent/new" element={<Navigate to="/runs" replace />} />
                <Route path="/reddit-agent/:agentId" element={<Navigate to="/runs" replace />} />
                {/* Default route - handled by AuthLayout */}
                <Route path="/" element={null} />
              </Route>
            </Routes>
          </StreamingProvider>
          <Toaster />
        </BrowserRouter>
        </ServerReadyGate>
      </QueryClientProvider>
    </PostHogProvider>
    </ThemeProvider>
  </StrictMode>,
)
